// @ts-nocheck Copypasted pendo script. TODO: Move init function to a separate file and ignore typecheck for it.

/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import Q from 'q';

import Cookie from 'js/lib/cookie';

const DEGREE_PARTNER_SHORTNAMES = ['asu', 'hec', 'illinois', 'london'] as const;

let deferred: Q.Deferred<void>;

export const PARTNER_API_KEY = 'd93226db-9e24-4d5f-5223-540c05442f1b';
export const ENTERPRISE_API_KEY = '63fb34da-80f5-4d22-588a-3f2a6ce5224e';
export const LEARNER_API_KEY = 'bfe8c8aa-f7a6-46f4-5ede-db5a212ce2cc';
const E2E_DISABLE_PENDO_FLAG = 'X-Coursera-Disable-Pendo';

type Cleanup = () => void;

const noop = () => {};

/**
 * Override pendo types to add support for "string[]". Remove when
 * https://github.com/DefinitelyTyped/DefinitelyTyped/pull/55323 is merged and released.
 */
interface Metadata {
  [key: string]: string | number | boolean | string[];
}

type IdentityMetadata = { id?: string | undefined } & Metadata;

interface Identity {
  /** visitor.id is required if user is logged in, otherwise an anonymous ID is generated and tracked by a cookie */
  visitor?: IdentityMetadata | undefined;
  account?: IdentityMetadata | undefined;
}

interface InitOptions extends Identity {
  apiKey?: string | undefined;
  excludeAllText?: boolean | undefined;
  excludeTitle?: boolean | undefined;
  disablePersistence?: boolean | undefined;
  guides?:
    | {
        delay?: boolean | undefined;
        disable?: boolean | undefined;
        timeout?: number | undefined;
        tooltip?:
          | {
              arrowSize?: number | undefined;
            }
          | undefined;
      }
    | undefined;
  events?: pendo.EventCallbacks | undefined;
}

/**
 * Helper to return consistent Account data for Pendo clients across Partner apps.
 * See `withPendoWrapper` for sample usage.
 */
export const getPartnerAccountData = ({
  partners = [],
  isSuperuser = false,
}: {
  partners?: Array<{ shortName: string }>;
  isSuperuser: boolean;
}): IdentityMetadata => {
  if (!partners || partners.length === 0) {
    return {};
  }

  const partnerShortNames = partners.map((partner) => partner.shortName);
  return {
    id: isSuperuser ? 'superuser' : partners[0].shortName, // pick the first partner as primary
    name: isSuperuser ? 'Coursera Superuser' : partners[0].name, // pick the first partner as primary
    partners: partnerShortNames,
    isDegreePartner: partnerShortNames.some((name) => DEGREE_PARTNER_SHORTNAMES.includes(name)),
  };
};

const injectPendoScriptTagIntoDOM = (
  windowObj: typeof window,
  document: Document,
  script: string,
  pendo: string,
  API_KEY: string,
  deferredPromise: Q.Deferred<any>
) => {
  /* eslint-disable */
  let v, w, x, y, z;
  let o = (windowObj[pendo] = windowObj[pendo] || {});
  o._q = [];
  v = ['initialize', 'identify', 'updateOptions', 'pageLoad'];
  for (w = 0, x = v.length; w < x; ++w)
    (function (m) {
      o[m] =
        o[m] ||
        function () {
          o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
        };
    })(v[w]);
  y = document.createElement(script);
  y.async = true;
  y.src = 'https://cdn.pendo.io/agent/static/' + API_KEY + '/pendo.js';
  y.onload = deferredPromise.resolve;
  z = document.getElementsByTagName(script)[0];
  z.parentNode.insertBefore(y, z);
  /* eslint-enable */
};

/**
 * Tracks guide readiness outside `window.pendo.events`, which may not exist until the script loads
 * and only notifies listeners that were registered before the guides loaded.
 */
let guidesHaveLoaded = false;
const guidesLoadedCallbacks = new Set<() => void>();

const flushGuidesLoaded = () => {
  guidesHaveLoaded = true;
  guidesLoadedCallbacks.forEach((callback) => callback?.());
  guidesLoadedCallbacks.clear();
};

type InitParams = {
  options?: InitOptions;
  API_KEY?: string;
  onGuidesLoadedCallback?: (pendo: pendo.Pendo) => void;
  disableInitIfPendoObjAlreadyExists?: boolean;
};
/**
 * pendo init function but allows initialization if another pendo object already exists in the window
 * @param initParams object parameter that takes in the following values
 * @param initParams.options Initialization options.
 * @param initParams.API_KEY The API key to use.
 * @param initParams.onGuidesLoadedCallback Optionally triggered when the Agent loads all guides. Returns back a valid Pendo object.
 * @param initParams.disableInitIfPendoObjAlreadyExists Boolean value to determine whether initialization should be blocked if a pendo object is already initialized in the window
 */
const initWithMultipleInstances = (initParams: InitParams) => {
  const { options, API_KEY, onGuidesLoadedCallback, disableInitIfPendoObjAlreadyExists } = initParams;
  // https://coursera.atlassian.net/browse/CP-6548
  // Disable initialization for requests coming from e2e test framework to prevent
  // flakiness in tests due to the pendo guides
  const disabled = Cookie.get(E2E_DISABLE_PENDO_FLAG) === 'true';
  if (
    typeof window === 'undefined' ||
    (typeof window.pendo !== 'undefined' && disableInitIfPendoObjAlreadyExists) ||
    disabled
  ) {
    return;
  }

  deferred = Q.defer();
  deferred.promise
    .then(() => {
      const pendo = window.pendo as pendo.Pendo | undefined;
      if (pendo) {
        pendo.initialize({
          ...options,
          events: {
            ...options?.events,
            guidesLoaded: () => {
              options?.events?.guidesLoaded?.();
              onGuidesLoadedCallback?.(pendo);
              // Run any callbacks registered via the module-level `onGuidesLoaded` helper.
              flushGuidesLoaded();
            },
          },
        });
        console.info('[pendo] initialized with data: ', JSON.stringify(options));
      }
    })
    .catch((e) => {
      Sentry.captureException(e, {
        tags: { coursera_domain: 'pendo' },
        extra: { message: 'Error initializing Pendo' },
      });
    });

  injectPendoScriptTagIntoDOM(window, document, 'script', 'pendo', API_KEY, deferred);
};

/**
 * Use this to initilize Pendo.
 * @param options Initialization options.
 * @param API_KEY The API key to use.
 * @param onGuidesLoadedCallback Optionally triggered when the Agent loads all guides. Returns back a valid Pendo object.
 */
const init = (options: InitOptions, API_KEY: string, onGuidesLoadedCallback?: (pendo: pendo.Pendo) => void) => {
  initWithMultipleInstances({
    options,
    API_KEY,
    onGuidesLoadedCallback,
    disableInitIfPendoObjAlreadyExists: true,
  });
};

const identify = (id: string) => {
  window.pendo?.identify(id);
};

const showGuideById = (id: string) => {
  window.pendo?.showGuideById?.(id);
};

type ShowGuideByIdWhenReadyParams = {
  id: string;
  onShowGuide?: () => void;
  onError?: (error: unknown) => void;
};

/**
 * Runs `callback` once Pendo's guides are loaded and available (e.g. right before `showGuideById`).
 * Safe to call before or after Pendo emits its `guidesLoaded` event. Each callback runs a single
 * time per page load; callers should still guard guide display across remounts. Returns a cleanup
 * function that cancels a callback that is still waiting for guides to load. No-op during SSR.
 */
const onGuidesLoaded = (callback: () => void): Cleanup => {
  if (typeof window === 'undefined') {
    return noop;
  }

  if (guidesHaveLoaded) {
    callback?.();
    return noop;
  } else {
    guidesLoadedCallbacks.add(callback);
    // Let React effect cleanups cancel pending guide work before Pendo finishes loading guides.
    return () => {
      guidesLoadedCallbacks.delete(callback);
    };
  }
};

/**
 * Shows a guide after Pendo's guide metadata has loaded. Use this instead of `showGuideById` when a
 * feature may request a guide on the same render that Pendo initializes; calling `showGuideById`
 * during that window can no-op because Pendo has not loaded its guides yet. Existing
 * `showGuideById` callers keep their immediate-attempt behavior, while this helper gives new
 * callers an explicit opt-in for deferred guide display.
 *
 * This follows the wrapper's non-critical Pendo behavior: it no-ops during SSR or when
 * `window.pendo.showGuideById` is unavailable, and it does not throw by default. Callers that need
 * cleanup or reporting can pass `onError`. Returns a cleanup function that cancels a queued guide
 * request if Pendo is still waiting for guide metadata.
 */
const showGuideByIdWhenReady = ({ id, onShowGuide, onError }: ShowGuideByIdWhenReadyParams): Cleanup => {
  const cleanupGuideRequest = onGuidesLoaded(() => {
    try {
      if (typeof window === 'undefined') {
        return;
      }

      if (!window.pendo?.showGuideById) {
        return;
      }

      window.pendo.showGuideById(id);
      onShowGuide?.();
    } catch (error) {
      onError?.(error);
    }
  });

  return cleanupGuideRequest;
};

/** Returns whether the Pendo agent has fully loaded and initialized. Defaults to false during SSR or when Pendo is not loaded. */
const isReady = () => {
  return typeof window !== 'undefined' && (window.pendo?.isReady?.() ?? false);
};

/** Merges new visitor or account metadata into the current Pendo session without re-initializing. No-op during SSR or when Pendo is not loaded. */
const updateOptions = (options: Identity) => {
  if (typeof window !== 'undefined') {
    window.pendo?.updateOptions(options);
  }
};

const pendo = {
  init,
  identify,
  showGuideById,
  showGuideByIdWhenReady,
  initWithMultipleInstances,
  isReady,
  updateOptions,
  onGuidesLoaded,
};

export default pendo;
